import { useCallback, useMemo, useRef, useState } from 'react';

export const CRITERIA_OPERATORS = [
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
  { value: '<', label: '<' },
  { value: '>', label: '>' },
  { value: '<=', label: '<=' },
  { value: '>=', label: '>=' },
  { value: 'CONTAINS', label: 'CONTAINS' },
  { value: 'IN', label: 'IN' },
];

/**
 * Centralized state and handlers for criteria editor panel + inline rename.
 */
export const useCriteriaEditor = ({ graphState, graphStateRef, applyGraphAction, showFeedback, graphActions }) => {
  const criterionIdRef = useRef(0);

  const [editNodeId, setEditNodeId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('unedited');
  const [editIncludeCriteria, setEditIncludeCriteria] = useState([]);
  const [editExcludeCriteria, setEditExcludeCriteria] = useState([]);

  const getNextCriterionId = useCallback(() => {
    criterionIdRef.current += 1;
    return `criterion-${criterionIdRef.current}`;
  }, []);

  const normalizeCriteriaForEdit = useCallback(
    (criteriaArray, fallbackCount = 0, group = 'include') => {
      if (Array.isArray(criteriaArray) && criteriaArray.length > 0) {
        return criteriaArray.map((criterion, index) => {
          if (typeof criterion === 'string') {
            return {
              id: getNextCriterionId(),
              field: criterion,
              operator: '=',
              value: '',
            };
          }

          if (criterion && typeof criterion === 'object') {
            const field = String(
              criterion.parameterName ||
                criterion.field ||
                criterion.label ||
                criterion.parameter ||
                criterion.name ||
                `Criterion ${index + 1}`,
            );
            const value = String(criterion.value || criterion.term || criterion.targetValue || '');
            const rawOperator = String(criterion.operator || '=').trim();
            const normalizedLookup = rawOperator.toLowerCase();
            const operator =
              normalizedLookup === 'equals'
                ? '='
                : normalizedLookup === 'not_equals'
                  ? '!='
                  : normalizedLookup === 'gte'
                    ? '>='
                    : normalizedLookup === 'lte'
                      ? '<='
                      : normalizedLookup === 'contains'
                        ? 'CONTAINS'
                        : normalizedLookup === 'in'
                          ? 'IN'
                          : rawOperator.toUpperCase();

            return {
              id: getNextCriterionId(),
              field,
              operator: CRITERIA_OPERATORS.some((entry) => entry.value === operator) ? operator : '=',
              value,
            };
          }

          return {
            id: getNextCriterionId(),
            field: `Criterion ${index + 1}`,
            operator: '=',
            value: '',
          };
        });
      }

      const safeCount = Number.isFinite(fallbackCount) ? fallbackCount : 0;
      return Array.from({ length: Math.max(0, safeCount) }, (_, index) => ({
        id: getNextCriterionId(),
        field: `${group === 'include' ? 'Include' : 'Exclude'} criterion ${index + 1}`,
        operator: '=',
        value: '',
      }));
    },
    [getNextCriterionId],
  );

  const editingNode = useMemo(
    () => (editNodeId ? graphState.nodesById?.[editNodeId] || null : null),
    [editNodeId, graphState.nodesById],
  );

  const closeEdit = useCallback(() => {
    setEditNodeId(null);
    setEditLabel('');
    setEditDescription('');
    setEditStatus('unedited');
    setEditIncludeCriteria([]);
    setEditExcludeCriteria([]);
  }, []);

  const openEdit = useCallback(
    (nodeId) => {
      const node = graphStateRef.current.nodesById?.[nodeId];
      if (!node) return;

      setEditNodeId(nodeId);
      setEditLabel(node.name || '');
      setEditDescription(node.description || '');
      setEditStatus(node.status || 'unedited');
      setEditIncludeCriteria(normalizeCriteriaForEdit(node.includeConditions, undefined, 'include'));
      setEditExcludeCriteria(normalizeCriteriaForEdit(node.excludeConditions, undefined, 'exclude'));
    },
    [graphStateRef, normalizeCriteriaForEdit],
  );

  const renameNode = useCallback(
    (nodeId, nextLabel) => {
      const trimmed = String(nextLabel || '').trim();
      if (!trimmed) return;

      applyGraphAction(
        {
          type: graphActions.UPDATE_NODE,
          payload: {
            nodeId,
            patch: {
              name: trimmed,
            },
          },
        },
        { recordHistory: true },
      );
    },
    [applyGraphAction, graphActions.UPDATE_NODE],
  );

  const addCriterion = useCallback(
    (group) => {
      const nextCriterion = {
        id: getNextCriterionId(),
        field: '',
        operator: '=',
        value: '',
      };

      if (group === 'include') {
        setEditIncludeCriteria((items) => [...items, nextCriterion]);
      } else {
        setEditExcludeCriteria((items) => [...items, nextCriterion]);
      }
    },
    [getNextCriterionId],
  );

  const updateCriterion = useCallback((group, criterionId, key, nextValue) => {
    const updater = (items) =>
      items.map((item) =>
        item.id === criterionId
          ? {
              ...item,
              [key]: nextValue,
            }
          : item,
      );

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const deleteCriterion = useCallback((group, criterionId) => {
    const updater = (items) => items.filter((item) => item.id !== criterionId);

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const saveEdit = useCallback(() => {
    if (!editNodeId) return false;

    const nextName = String(editLabel || '').trim();
    if (!nextName) {
      showFeedback('Node label is required.', 'warning');
      return false;
    }

    const normalizeForSave = (items) =>
      items
        .map((item) => ({
          parameterName: String(item.field || '').trim(),
          operator: String(item.operator || '='),
          value: String(item.value || '').trim(),
          logicalOperator: 'AND',
        }))
        .filter((item) => item.parameterName.length > 0 || item.value.length > 0);

    const includeConditions = normalizeForSave(editIncludeCriteria);
    const excludeConditions = normalizeForSave(editExcludeCriteria);

    const hasCriteria = includeConditions.length > 0 || excludeConditions.length > 0;
    const resolvedStatus = editStatus === 'unedited' && hasCriteria ? 'edited' : editStatus;
    const executed = resolvedStatus === 'executed' || resolvedStatus === 'completed';
    const currentPatientCount = graphStateRef.current.nodesById?.[editNodeId]?.patientCount;

    const changed = applyGraphAction(
      {
        type: graphActions.UPDATE_NODE,
        payload: {
          nodeId: editNodeId,
          patch: {
            name: nextName,
            description: String(editDescription || '').trim(),
            status: resolvedStatus,
            includeConditions,
            excludeConditions,
            isExecuted: executed,
            patientCount: executed
              ? Number.isFinite(currentPatientCount)
                ? Number(currentPatientCount)
                : 123
              : null,
          },
        },
      },
      { recordHistory: true },
    );

    if (!changed) return false;

    closeEdit();
    showFeedback('Node updated.', 'success');
    return true;
  }, [
    applyGraphAction,
    closeEdit,
    editDescription,
    editExcludeCriteria,
    editIncludeCriteria,
    editLabel,
    editNodeId,
    editStatus,
    graphActions.UPDATE_NODE,
    graphStateRef,
    showFeedback,
  ]);

  return {
    editingNode,
    editNodeId,
    editLabel,
    editDescription,
    editStatus,
    editIncludeCriteria,
    editExcludeCriteria,
    criteriaOperators: CRITERIA_OPERATORS,
    setEditLabel,
    setEditDescription,
    setEditStatus,
    openEdit,
    closeEdit,
    renameNode,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    saveEdit,
  };
};

export default useCriteriaEditor;
